import React from "react";
import { BrowserRouter, Routes, Route, Navigate, matchPath } from "react-router-dom";
import { useSelector } from "react-redux";

/**
 * Pages
 */
import LayoutPage from "../../Pages/Layout/LayoutPage";
import HomePage from "../../Pages/Home/HomePage";
import UserPage from "../../Pages/User/UserPage";
import UserDetailPage from "../../Pages/User/UserDetailPage";
import SegmentPage from "../../Pages/Segment/SegmentPage";
import CategoryPage from "../../Pages/Category/CategoryPage";
import BrandPage from "../../Pages/Brand/BrandPage";
import SubcategoryPage from "../../Pages/Subcategory/SubcategoryPage";
import SubSubcategoryPage from "../../Pages/SubSubcategory/SubSubcategoryPage";
import AssemblePage from "../../Pages/Assembly/AssemblyPage";
import LanguagePage from "../../Pages/Language/LanguagePage";
import ProductPage from "../../Pages/Products/ProductPage";
import SkuDetailPage from "../../Pages/SKU/SkuDetailPage";
import SkuPage from "../../Pages/SKU/SkuPage";
import ColorPage from "../../Pages/Color/ColorPage";
import CountryPage from "../../Pages/Country/CountryPage";
import CertificatePage from "../../Pages/Certificate/CertificatePage";
import ElectricalRatingPage from "../../Pages/ElectricalRating/ElectricalRatingPage";
import PartChangeLocationPage from "../../Pages/PartChangeLocation/PartChangeLocationPage";
import RolePage from "../../Pages/Role/RolePage";
import RoleDetailPage from "../../Pages/Role/RoleDetailPage";
import ProductMasterPage from "../../Pages/Products/ProductMasterPage";
import ProductDetailEditPage from "../../Pages/Products/ProductDetailEditPage";
import AppListPage from "../../Pages/App/AppListPage";
import ImageFuncPage from "../../Pages/App/ImageFuncPage";
import VideoFuncPage from "../../Pages/App/VideoFuncPage";
import ProductColorPage from "../../Pages/ProductColor/ProductColorPage";
import TagPage from "../../Pages/Tag/TagPage";
import ProductTagPage from "../../Pages/ProductTag/ProductTagPage";
import ComBrandPage from "../../Pages/ComBrand/ComBrandPage";
import ComProductPage from "../../Pages/ComProduct/ComProductPage";
import ComProductMapPage from "../../Pages/ComProductMap/ComProductMapPage";
import NotFound from "../../Pages/Error/NotFound";
import SeriesPage from "../../Pages/Series/SeriesPage";
import IoTSeriesPage from "../../Pages/IoTSeries/IoTSeriesPage";
import StoreOnlinePage from "../../Pages/StoreOnline/StoreOnlinePage";
import FeaturePage from "../../Pages/Feature/FeaturePage";
import LogsPage from "../../Pages/Logs/LogsPage";
import ProductDetailFormPage from "../../Pages/Products/ProductDetailFormPage";
import ApprovalPage from "../../Pages/Approval/ApprovalPage";
import MasterReportBuilderPage from "../../Pages/MasterReportBuilder/MasterReportBuilder";
import FormMasterReport from "../../Pages/MasterReportBuilder/FormMasterReport";
import FormExportData from "../../Pages/MasterReportBuilder/FormExportData";
import ListGenerateProductData from "../../Pages/MasterReportBuilder/ListGenerateProductData";
import ScheduledPublishedData from "../../Pages/ScheduledPublishedData/ScheduledPublishedData";
import HistoryExportedData from "../../Pages/HistoryExportedData/HistoryExportedData";
import ProductPageView from "../../Pages/ProductView/ProductPageView";
import ProductDetailView from "../../Pages/ProductView/ProductDetailView";

const routeList = [
  "/home",
  "/dashboard",
  "/user",
  "/user/:pageState/:id",
  "/role",
  "/role/:pageState/:role_id",
  "/app-list",
  "/image-function",
  "/video-function",
  "/segment",
  "/category",
  "/brand",
  "/subcategory",
  "/subsubcategory",
  "/assembly",
  "/language",
  "/color",
  "/country",
  "/tag",
  "/certificate",
  "/electrical-rating",
  "/part-change-location",
  "/sku/:pageState",
  "/sku/:pageState/:id/prefix/:currentPrefix",
  "/sku",
  "/product/:pageState/:item_code",
  "/product/:pageState/new",
  "/product/edit/:item_code",
  "/product",
  "/product-color",
  "/product-tag",
  "/competitor-brand",
  "/competitor-product",
  "/competitor-mapping",
  "/series",
  "/iot-series",
  "/store-online",
  "/feature",
  "/product-detail-form/:country_code/:item_code/",
  "/logs",
  "/approvals",
  "/product-view",
  "/product-view/detail/:country_code/:item_code", 
  // "/master-reports",
  // "/master-reports/create",
  // "/master-reports/:id/update",
  // "/master-export-data",
  // "/list-exported-data/:id",
  // "/exported-data",
  // "/history-exported-data",
];

const tryRequire = (path) => {
  let match = 0;

  for (let key in routeList) {
    if (matchPath(routeList[key], path)) {
      match++;
    }
  }

  if (match > 0) {
    return true;
  } else {
    return false;
  }
};

const ToHome = () => {
  const refreshPath = useSelector(({ auth: { refreshPath } }) => refreshPath);
  const path = refreshPath.replace("login", "dashboard").split("/").slice(0, 6).join("/");

  if (tryRequire(path)) {
    return <Navigate to={path} replace={true} />;
  } else {
    return <Navigate to="/not-found" replace={true} />;
  }
};

export const PagesNavigation = () => {
  const { userInfo } = useSelector((state) => state.auth);
  //console.log(userInfo);
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LayoutPage />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          {userInfo?.access?.dashboard?.can_view && <Route path="/dashboard" element={<HomePage />} />}
          <Route path="/user/:pageState/:id" element={<UserDetailPage />} />

          <>
            {userInfo?.access?.users?.can_view && <Route path="/user" element={<UserPage />} />}
            {userInfo?.access?.role_user?.can_view && <Route path="/role" element={<RolePage />} />}
            {userInfo?.access?.role_user?.can_view && <Route path="/role/:pageState/:role_id" element={<RoleDetailPage />} />}
            {userInfo?.access?.app_list?.can_view && <Route path="/app-list" element={<AppListPage />} />}
            {userInfo?.access?.image_function?.can_view && <Route path="/image-function" element={<ImageFuncPage />} />}
            {userInfo?.access?.video_function?.can_view && <Route path="/video-function" element={<VideoFuncPage />} />}
          </>

          <>{userInfo?.access?.approvals?.can_view && <Route path="/approvals" element={<ApprovalPage />} />}</>

          <>
            {userInfo?.access?.master_segment?.can_view && <Route path="/segment" element={<SegmentPage />} />}
            {userInfo?.access?.master_category?.can_view && <Route path="/category" element={<CategoryPage />} />}
            {userInfo?.access?.master_brand?.can_view && <Route path="/brand" element={<BrandPage />} />}
            {userInfo?.access?.master_subcategory?.can_view && <Route path="/subcategory" element={<SubcategoryPage />} />}
            {userInfo?.access?.master_subsubcategory?.can_view && <Route path="/subsubcategory" element={<SubSubcategoryPage />} />}
            {userInfo?.access?.master_store_onlune?.can_view && <Route path="/store-online" element={<StoreOnlinePage />} />}
            {userInfo?.access?.master_assembly?.can_view && <Route path="/assembly" element={<AssemblePage />} />}
            {userInfo?.access?.master_language?.can_view && <Route path="/language" element={<LanguagePage />} />}
            {userInfo?.access?.master_color?.can_view && <Route path="/color" element={<ColorPage />} />}
            {userInfo?.access?.master_country?.can_view && <Route path="/country" element={<CountryPage />} />}
            {userInfo?.access?.master_tag?.can_view && <Route path="/tag" element={<TagPage />} />}
            {userInfo?.access?.master_certification?.can_view && <Route path="/certificate" element={<CertificatePage />} />}
            {userInfo?.access?.master_electrical_rating?.can_view && <Route path="/electrical-rating" element={<ElectricalRatingPage />} />}
            {userInfo?.access?.master_part_change?.can_view && <Route path="/part-change-location" element={<PartChangeLocationPage />} />}
            {userInfo?.access?.master_series?.can_view && <Route path="/series" element={<SeriesPage />} />}
            {userInfo?.access?.master_series?.can_view && <Route path="/iot-series" element={<IoTSeriesPage />} />}
            {userInfo?.access?.master_store_online?.can_view && <Route path="/store-online" element={<StoreOnlinePage />} />}
            {userInfo?.access?.master_feature?.can_view && <Route path="/feature" element={<FeaturePage />} />}
          </>

          {userInfo?.access?.sku_config?.can_view && (
            <>
              <Route path="/sku/:pageState" element={<SkuDetailPage />} />
              <Route path="/sku/:pageState/:id/prefix/:currentPrefix" element={<SkuDetailPage />} />
              <Route path="/sku" element={<SkuPage />} />
            </>
          )}

          <>
            {userInfo?.access?.master_product?.can_view && <Route path="/product/:pageState/:item_code" element={<ProductMasterPage />} />}
            {userInfo?.access?.master_product?.can_view && <Route path="/product/:pageState/new" element={<ProductMasterPage />} />}
            {userInfo?.access?.master_product?.can_update && <Route path="/product/edit/:item_code" element={<ProductDetailEditPage />} />}
            {userInfo?.access?.master_product?.can_view && <Route path="/product" element={<ProductPage />} />}
            {userInfo?.access?.master_product_view?.can_view && <Route path="/product-view" element={<ProductPageView />} />}
            {userInfo?.access?.master_product_view?.can_view && <Route path="/product-view/detail/:country_code/:item_code" element={<ProductDetailView />} />}
            {userInfo?.access?.master_product?.can_update && <Route path="/product-detail-form/:country_code/:item_code" element={<ProductDetailFormPage />} />}
            {userInfo?.access?.product_color?.can_view && <Route path="/product-color" element={<ProductColorPage />} />}
            {userInfo?.access?.product_tag?.can_view && <Route path="/product-tag" element={<ProductTagPage />} />}
          </>

          <>
            {userInfo?.access?.competitor_brand?.can_view && <Route path="/competitor-brand" element={<ComBrandPage />} />}
            {userInfo?.access?.competitor_product?.can_view && <Route path="/competitor-product" element={<ComProductPage />} />}
            {userInfo?.access?.competitor_mapping_product?.can_view && <Route path="/competitor-mapping" element={<ComProductMapPage />} />}
          </>

          {userInfo?.access?.logs?.can_view && (
            <>
              <Route path="/logs" element={<LogsPage />} />
            </>
          )}

          {/* {userInfo?.access?.master_product?.can_view && (
            <>
              <Route
                path="/master-reports"
                element={<MasterReportBuilderPage />}
              />
              <Route
                path="/master-reports/create"
                element={<FormMasterReport />}
              />
              <Route
                path="/master-reports/:id/update"
                element={<FormMasterReport />}
              />
              <Route
                path="/master-export-data"
                element={<FormExportData />}
              />
              <Route
                path="/list-exported-data/:id"
                element={<ListGenerateProductData />}
              />
              <Route
                path="/exported-data"
                element={<ScheduledPublishedData />}
              />
              <Route
                path="/history-exported-data"
                element={<HistoryExportedData />}
              />
            </>

          )} */}

          <Route path="/not-found" element={<NotFound />} />
          <Route path="/*" element={<ToHome />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default PagesNavigation;
